/**
 * App shell for the purpose-built web UI (dark ink + amber design
 * language, ui/theme.css).
 *
 * Layout: left sidebar (brand mark, project switcher, nav from the route
 * registry, user menu) + top bar (active route title + slot for screen
 * actions) + content area rendering the active screen via the minimal
 * hash router. Collapses to an overlay drawer under 860px.
 *
 * Exposes `useProject()` — the active-project context every screen
 * consumes (see web/README-UI.md).
 */
import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import {
  Check,
  ChevronsUpDown,
  LogOut,
  Menu as MenuIcon,
  Plus,
} from "lucide-react"
import type { ProjectEntry } from "@llm-wiki/shared"
import { useAuth } from "./auth-context"
import { client } from "./api/client"
import { ROUTES, useRoute } from "./router"
import { LanguageSync } from "./i18n/react"
import { useTranslation } from "react-i18next"
import {
  Button,
  Field,
  IconButton,
  Input,
  Modal,
  Spinner,
  ToastProvider,
  useToast,
} from "./ui/primitives"

/* ── Active-project context ─────────────────────────────────────────── */

export interface ProjectContextValue {
  /** Active project, or null while loading / when none exists yet. */
  project: ProjectEntry | null
  /** All projects visible to the caller. */
  projects: ProjectEntry[]
  loading: boolean
  /** Switch project (records server-side MRU). */
  select: (projectId: string) => Promise<void>
  /** Re-fetch the project list (e.g. after create). */
  refresh: () => Promise<void>
}

const ProjectContext = createContext<ProjectContextValue | null>(null)

export function useProject(): ProjectContextValue {
  const ctx = useContext(ProjectContext)
  if (!ctx) throw new Error("useProject must be used inside <AppShell>")
  return ctx
}

/* ── Click-outside helper for the two dropdown menus ────────────────── */

function useClickOutside(onOutside: () => void) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onOutside()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOutside()
    }
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [onOutside])
  return ref
}

/* ── Project switcher (sidebar top) ─────────────────────────────────── */

function ProjectSwitcher({ onNavigate }: { onNavigate: () => void }) {
  const { project, projects, loading, select, refresh } = useProject()
  const { toast } = useToast()
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [name, setName] = useState("")
  const [busy, setBusy] = useState(false)
  const close = useCallback(() => setOpen(false), [])
  const menuRef = useClickOutside(close)

  async function handleCreate() {
    const trimmed = name.trim()
    if (trimmed === "" || busy) return
    setBusy(true)
    try {
      const res = await client.projects.create({ name: trimmed })
      await refresh()
      await select(res.project.id)
      setCreateOpen(false)
      setName("")
      toast(t("shell.project_created", { name: res.project.name }), { tone: "success" })
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), { tone: "error" })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div ref={menuRef} style={{ position: "relative", marginBottom: 10 }}>
      <button
        type="button"
        className="nav-item"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          border: "1px solid var(--lw-border)",
          background: "var(--lw-input-bg)",
          justifyContent: "space-between",
          color: "var(--lw-text)",
          fontWeight: 600,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {loading ? t("shell.loading_projects") : (project?.name ?? t("shell.select_project"))}
        </span>
        <ChevronsUpDown size={14} style={{ flex: "none", color: "var(--lw-faint)" }} />
      </button>

      {open && (
        <div className="menu" role="listbox" style={{ left: 0, right: 0, top: "calc(100% + 6px)" }}>
          {projects.length === 0 && !loading && (
            <div style={{ padding: "8px 10px", fontSize: 13, color: "var(--lw-muted)" }}>
              {t("shell.no_projects")}
            </div>
          )}
          {projects.map((p) => (
            <button
              key={p.id}
              type="button"
              role="option"
              aria-selected={p.id === project?.id}
              className="menu-item"
              onClick={() => {
                setOpen(false)
                if (p.id !== project?.id) {
                  void select(p.id).then(onNavigate)
                }
              }}
            >
              <span style={{ width: 16, flex: "none" }}>
                {p.id === project?.id && <Check size={14} color="var(--lw-accent)" />}
              </span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</span>
            </button>
          ))}
          <div className="menu-separator" />
          <button
            type="button"
            className="menu-item"
            onClick={() => {
              setOpen(false)
              setCreateOpen(true)
            }}
          >
            <Plus size={14} style={{ color: "var(--lw-accent)" }} />
            {t("shell.new_project")}
          </button>
        </div>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t("shell.new_project")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={name.trim() === ""}
              onClick={() => void handleCreate()}
            >
              {t("shell.create_project")}
            </Button>
          </>
        }
      >
        <Field label={t("shell.project_name")} htmlFor="new-project-name">
          <Input
            id="new-project-name"
            value={name}
            autoFocus
            placeholder={t("shell.project_name_placeholder")}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleCreate()
            }}
          />
        </Field>
      </Modal>
    </div>
  )
}

/* ── User menu (sidebar bottom) ─────────────────────────────────────── */

function UserMenu() {
  const { user, logout } = useAuth()
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const close = useCallback(() => setOpen(false), [])
  const ref = useClickOutside(close)
  const [version, setVersion] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch("/healthz")
      .then((r) => r.json() as Promise<{ version?: string }>)
      .then((d) => {
        if (!cancelled && typeof d.version === "string") setVersion(d.version)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const initial = (user?.email ?? "?").slice(0, 1).toUpperCase()

  return (
    <div ref={ref} style={{ position: "relative", marginTop: "auto", paddingTop: 12 }}>
      <button
        type="button"
        className="nav-item"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{ color: "var(--lw-text)" }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 26,
            height: 26,
            borderRadius: 8,
            background: "var(--lw-ink-3)",
            border: "1px solid var(--lw-border)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
            color: "var(--lw-accent)",
            flex: "none",
          }}
        >
          {initial}
        </span>
        <span
          style={{
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            lineHeight: 1.25,
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {user?.email}
          </span>
          <span style={{ fontSize: 11, color: "var(--lw-faint)" }}>
            {user?.role}
            {version !== null && ` · v${version}`}
          </span>
        </span>
      </button>

      {open && (
        <div className="menu" role="menu" style={{ left: 0, right: 0, bottom: "calc(100% + 6px)" }}>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() => void logout()}
          >
            <LogOut size={14} />
            {t("shell.sign_out")}
          </button>
        </div>
      )}
    </div>
  )
}

/* ── Shell ──────────────────────────────────────────────────────────── */

function ShellInner() {
  const { user } = useAuth()
  const { t } = useTranslation()
  const [route, go] = useRoute()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const isAdmin = user?.role === "admin"
  const visibleRoutes = ROUTES.filter((r) => !r.adminOnly || isAdmin)
  const active =
    visibleRoutes.find((r) => r.id === route) ?? visibleRoutes[0]
  const Screen = active.component

  return (
    <div className="shell">
      {drawerOpen && (
        <div className="sidebar-scrim" onClick={() => setDrawerOpen(false)} />
      )}

      <nav
        className={`sidebar${drawerOpen ? " open" : ""}`}
        aria-label={t("shell.primary_nav")}
      >
        <div className="brand">
          <img src="/logo-mark.svg" width={26} height={26} alt="" aria-hidden="true" style={{ borderRadius: 7 }} />
          LLM Wiki
        </div>

        <ProjectSwitcher onNavigate={() => setDrawerOpen(false)} />

        {visibleRoutes.map((r) => {
          const Icon = r.icon
          return (
            <button
              key={r.id}
              type="button"
              className="nav-item"
              aria-current={r.id === active.id ? "page" : undefined}
              onClick={() => {
                go(r.id)
                setDrawerOpen(false)
              }}
            >
              <Icon aria-hidden="true" />
              {t(`nav.${r.id}`, { defaultValue: r.label })}
            </button>
          )
        })}

        <UserMenu />
      </nav>

      <div className="shell-main">
        <header className="topbar">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <IconButton
              label={t("shell.open_navigation")}
              className="sidebar-toggle"
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon size={16} />
            </IconButton>
            <span className="topbar-title">{t(`nav.${active.id}`, { defaultValue: active.label })}</span>
          </div>
          {/* Right-side actions: screens render their own controls in-page;
              this slot stays for global actions (kept intentionally quiet). */}
          <div id="topbar-actions" style={{ display: "flex", gap: 8 }} />
        </header>

        <main className="shell-content" key={active.id}>
          <Suspense
            fallback={
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 240,
                }}
              >
                <Spinner size="lg" />
              </div>
            }
          >
            <Screen />
          </Suspense>
        </main>
      </div>
    </div>
  )
}

export function AppShell() {
  const { user } = useAuth()
  const [projects, setProjects] = useState<ProjectEntry[]>([])
  const [current, setCurrent] = useState<ProjectEntry | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    const res = await client.projects.list()
    setProjects(res.projects)
    setCurrent((prev) => {
      // Keep an explicit selection; otherwise adopt the server's current
      // (per-user last-opened), else the first project.
      const keep = prev && res.projects.find((p) => p.id === prev.id)
      return keep ?? res.currentProject ?? res.projects[0] ?? null
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    refresh()
      .catch(() => {
        /* screens surface their own load errors; the switcher just stays empty */
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [refresh, user?.id])

  const select = useCallback(
    async (projectId: string) => {
      const next = projects.find((p) => p.id === projectId) ?? null
      setCurrent(next)
      try {
        await client.projects.open(projectId) // per-user MRU
      } catch {
        // MRU write is best-effort; the switch itself already happened
      }
    },
    [projects],
  )

  const value = useMemo<ProjectContextValue>(
    () => ({ project: current, projects, loading, select, refresh }),
    [current, projects, loading, select, refresh],
  )

  return (
    <div className="lw-root">
      <LanguageSync />
      <ToastProvider>
        <ProjectContext.Provider value={value}>
          <ShellInner />
        </ProjectContext.Provider>
      </ToastProvider>
    </div>
  )
}

export default AppShell
